/**
 * The component which is set on the p2p react overpanel and handles the theme selection for gift/theme flows.
 *
 * TIP: When used with the react overpanel, the overpanel should be set to the preset 'FULL' and should be given a
 * contentClassName override. That string override should contain a class that sets padding: 0 and has col-xs-12. This
 * is so the background gradient extends fully to the edges of the viewport.
 */

import { connect } from 'transfer/utils/p2p-connect'
import PropTypes from 'prop-types'
import React from 'react'
import { css } from 'lib/paypalphrodite'
import { Motion, spring, presets } from 'react-motion'
import ThemeCard from './theme-card'
import styles from './theme-selection.styles'
import { getThemeImage } from '../utils/theme-utils'
import * as fpti from '../../lib/analytics/fpti'
import content from 'pp-react-l10n'
let i18n = content('transfer/themes')

/**
 * ThemeIcons are the circle buttons you click to select a theme/card
 */

function ThemeIcon({
  styleToInterpolate,
  iconImageUrl,
  mainColor,
  changeSelectedCard,
  id,
  name,
}) {
  return (
    <Motion style={styleToInterpolate}>
      {interpolatedStyles => {
        const buttonWrapperStyle = {
          opacity: interpolatedStyles.opacity,
        }

        const iconStyle = {
          transform: `scale(${interpolatedStyles.scale})`,
          backgroundImage: `url("${iconImageUrl}")`,
        }

        return (
          <button
            className={`${css(styles.buttonEl)} unit-test-theme-icon`}
            style={buttonWrapperStyle}
            data-a11y-overpanel-focus-target={`theme-icon-${id}`}
            onClick={changeSelectedCard}
            aria-label={i18n('a11y.icon.name', { name })}
          >
            <div style={iconStyle} className={css(styles.themeIcon)} />
          </button>
        )
      }}
    </Motion>
  )
}
ThemeIcon.propTypes = {
  styleToInterpolate: PropTypes.object,
  iconImageUrl: PropTypes.string.isRequired,
  mainColor: PropTypes.string.isRequired,
  changeSelectedCard: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
}

export class ThemeSelection extends React.Component {
  static propTypes = {
    displayName: PropTypes.string.isRequired,
    formattedAmount: PropTypes.string.isRequired,
    handleNextButton: PropTypes.func.isRequired, // should be used by parent to save theme, and close overpanel
    handleCancelButton: PropTypes.func.isRequired, // should be used by parent to clear theme, and close overpanel
    handleRemoveButton: PropTypes.func.isRequired, // should be used by parent to clear theme, and close overpanel
    handleChangeNote: PropTypes.func.isRequired,
    themeList: PropTypes.array.isRequired,
    note: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    savedThemeExists: PropTypes.bool,
    themeIsMandatory: PropTypes.bool,
  }

  static defaultProps = {
    note: '',
    thumbnailUrl: '',
  }

  state = {
    cardHasFlippedOnce: false,
    selectedThemeIndex: 0,
    selectedCardIsFlipped: false,
    cardFrontIsHovered: false,
  }

  changeSelectedCard = newIndex => {
    this.setState({
      selectedThemeIndex: newIndex,
      selectedCardIsFlipped: false,
    })

    const pageGroup = 'main:consumer:p2p:gift:theme'
    const pageName = `main:consumer:p2p:gift:theme:${newIndex}`

    // add analytics for theme selection
    fpti.trackLink('theme', pageGroup, pageName)
  }

  flipSelectedCard = () => {
    this.setState({
      selectedCardIsFlipped: !this.state.selectedCardIsFlipped,
      cardHasFlippedOnce: true,
    })
  }

  handleCardFrontHover = () => {
    this.setState({
      cardFrontIsHovered: true,
    })
  }

  handleCardFrontBlur = () => {
    this.setState({
      cardFrontIsHovered: false,
    })
  }

  handleSubmitButton = () => {
    if (!this.state.cardHasFlippedOnce) {
      return this.flipSelectedCard()
    }
    return this.props.handleNextButton(
      this.props.themeList[this.state.selectedThemeIndex].id
    )
  }

  /**
   * Get the react-motion style values for the ThemeIcons
   */
  getThemeIconValuesToInterpolate = currentIndex => {
    const { selectedThemeIndex } = this.state
    return {
      scale: spring(
        currentIndex === selectedThemeIndex ? 1.1 : 1,
        presets.wobbly
      ),
      opacity: spring(currentIndex === selectedThemeIndex ? 1 : 0.4),
    }
  }

  /**
   * Get the react-motion style values that <Motion/> will interpolate to from whatever previous style values it was given.
   * The cards are assigned styles based on the order in the "stack" relative to the chosen card. Cards "above" the
   * selected card are given styles so they appear to drop in from above. Cards "below" are styled to rise up from below.
   */
  getCardValuesToInterpolate = currentIndex => {
    const {
      selectedThemeIndex,
      selectedCardIsFlipped,
      cardFrontIsHovered,
    } = this.state
    const hoverRotation = cardFrontIsHovered ? 7 : 0
    const cardStyle = {}
    if (currentIndex > selectedThemeIndex) {
      // positioned to appear above current card
      cardStyle.opacity = spring(0)
      cardStyle.scale = spring(1.1)
      const posOrNeg = currentIndex % 2 === 0 ? 1 : -1
      const rotationAmount = posOrNeg * 3
      cardStyle.rotation = spring(rotationAmount)
      cardStyle.yRotation = spring(0)
    } else if (currentIndex === selectedThemeIndex) {
      // this is the selected card, make it prime viewing!
      cardStyle.opacity = spring(1)
      cardStyle.scale = spring(1, { stiffness: 220, damping: 12 })
      cardStyle.rotation = spring(0)
      const yRotationAmount = selectedCardIsFlipped ? 180 : 0 + hoverRotation
      cardStyle.yRotation = spring(yRotationAmount, {
        stiffness: 50,
        damping: 8,
      })
    } else if (currentIndex < selectedThemeIndex) {
      // positioned to appear below current card
      cardStyle.opacity = spring(0)
      cardStyle.scale = spring(0.9)
      cardStyle.rotation = spring(0)
      cardStyle.yRotation = spring(0)
    }
    return cardStyle
  }

  /**
   * Get the styles for the wrapper element that wraps both card front and card back
   */
  getCardWrapperStyles = (interpolatedStyles, currentIndex) => {
    const isSelectedCard = currentIndex === this.state.selectedThemeIndex
    return {
      visibility: isSelectedCard ? 'visible' : 'hidden',
      opacity: interpolatedStyles.opacity,
      transform: `scale(${interpolatedStyles.scale}) rotate(${interpolatedStyles.rotation}deg)`,
      zIndex: isSelectedCard && 1000, // required for IE — hidden cards can block clicks if above in stack, ensure clickability
      pointerEvents: !isSelectedCard && 'none', // required for firefox — hidden cards can block clicks if above in stack, ensure clickability
    }
  }

  /**
   * Get the styles for the element serves as the front of the card.
   * Mainly used to flip the card. In most browsers flipping the wrapper altogether would be fine,
   * but in IE flipping each card face (giving the illusion that the wrapper is flipping) is required.
   */
  getCardFrontStyles = ({ yRotation }, currentIndex) => {
    return {
      backgroundColor: this.props.themeList[currentIndex].mainColor,
      backgroundImage: `url("${getThemeImage(
        this.props.themeList[currentIndex].id,
        'card'
      )}")`,
      backgroundSize: 'cover',
      transform: `rotateY(${yRotation}deg)`,
    }
  }

  /**
   * Get the styles for the element serves as the back of the card.
   * Mainly used to flip the card. In most browsers flipping the wrapper altogether would be fine,
   * but in IE flipping each card face (giving the illusion that the wrapper is flipping) is required.
   */
  getCardBackStyles = ({ yRotation }) => {
    return {
      transform: `rotateY(${180 + yRotation}deg)`,
    }
  }

  /**
   * Get the gradient and opacity for the background based on the current theme
   */
  getBackgroundStyles = currentIndex => {
    const { selectedThemeIndex } = this.state
    return {
      opacity: currentIndex === selectedThemeIndex ? 1 : 0,
      background: this.props.themeList[currentIndex].gradient,
    }
  }

  /**
   * Helper to pull the svg and styles out of the render function
   */
  renderFlipIcon = () => {
    return (
      <svg className={css(styles.flipIcon)} viewBox="0 0 23 27">
        <path
          d="M15.55,4.5h-4A11,11,0,1,0,22,12.25"
          transform="translate(0.01)"
        />
        <polyline points="12.01 8.5 16.01 4.5 12.01 0.5" />
      </svg>
    )
  }

  render() {
    const {
      note,
      displayName,
      thumbnailUrl,
      formattedAmount,
      handleChangeNote,
      handleCancelButton,
      handleRemoveButton,
      savedThemeExists,
      themeIsMandatory,
    } = this.props
    const { selectedThemeIndex } = this.state
    const { designer, themeName = '' } =
      this.props.themeList[selectedThemeIndex] || {}

    return (
      <div ref={c => (this.containerEl = c)} className={css(styles.wrapper)}>
        {/* backgrounds: because they're gradients and can't transition, we need one for each theme to transition opacity */}
        {this.props.themeList.map((_, i) => (
          <div
            key={i}
            style={this.getBackgroundStyles(i)}
            className={css(styles.background)}
          />
        ))}

        {/* foreground: given position to sit on backgrounds */}
        <div className={css(styles.foreground)}>
          <h2 className={css(styles.header)}>{i18n('selection.header')}</h2>
          <div>
            <p className={css(styles.attribution)}>
              {i18n.html('selection.attribution', { designer })}
            </p>
          </div>

          {/* "centerPiece" is the ThemeIcons, cards, and flip button */}
          <div className={css(styles.centerPiece)}>
            {/* the theme icon buttons */}
            <div className={css(styles.iconsWrapper)}>
              {this.props.themeList.map(({ id, mainColor, name }, i) => (
                <ThemeIcon
                  key={i}
                  id={i}
                  styleToInterpolate={this.getThemeIconValuesToInterpolate(i)}
                  changeSelectedCard={() => this.changeSelectedCard(i)}
                  iconImageUrl={getThemeImage(id, 'icon')}
                  mainColor={mainColor}
                  name={name}
                />
              ))}
            </div>

            {/* the stack of cards */}
            <div className={css(styles.cardGroupWrapper)}>
              <div
                role="region"
                aria-live="assertive"
                aria-label={i18n('a11y.theme.selected', { themeName })}
              />
              {this.props.themeList.map(({ id, mainColor }, i) => (
                <Motion key={i} style={this.getCardValuesToInterpolate(i)}>
                  {interpolatedStyles => {
                    return (
                      <ThemeCard
                        themeId={id}
                        cardWrapperStyle={this.getCardWrapperStyles(
                          interpolatedStyles,
                          i
                        )}
                        cardFrontStyle={this.getCardFrontStyles(
                          interpolatedStyles,
                          i
                        )}
                        cardBackStyle={this.getCardBackStyles(
                          interpolatedStyles
                        )}
                        handleCardFrontClick={this.flipSelectedCard}
                        handleCardFrontHover={this.handleCardFrontHover}
                        handleCardFrontBlur={this.handleCardFrontBlur}
                        editableMessage={true}
                        isNoteFieldTabEnabled={
                          i === this.state.selectedThemeIndex &&
                          this.state.selectedCardIsFlipped
                        }
                        note={note}
                        handleChangeNote={handleChangeNote}
                        displayName={displayName}
                        formattedAmount={formattedAmount}
                        thumbnailUrl={thumbnailUrl}
                        footerThemeImageUrl={getThemeImage(id, 'footer')}
                      />
                    )
                  }}
                </Motion>
              ))}
            </div>

            {/* the flip button */}
            <button
              className={`${css(styles.flipWrapper)} unit-test-flip-button`}
              onClick={this.flipSelectedCard}
              aria-label={i18n('a11y.flip.card')}
            >
              {this.renderFlipIcon()}
            </button>
          </div>

          <button
            onClick={this.handleSubmitButton}
            data-nemo="gift-overpanel-next"
            className={`${css(
              styles.nextButton
            )} vx_btn vx_btn-secondary_reversed`}
          >
            {this.state.cardHasFlippedOnce
              ? i18n('selection.save')
              : i18n('selection.next')}
          </button>

          {!themeIsMandatory && savedThemeExists && (
            <button
              className={`${css(
                styles.removeButton
              )} vx_btn-link unit-test-remove-button`}
              onClick={handleRemoveButton}
            >
              {i18n('selection.remove')}
            </button>
          )}

          <button
            className={`${css(styles.cancelButton)} vx_btn-link`}
            onClick={handleCancelButton}
          >
            {i18n('selection.cancel')}
          </button>
        </div>
      </div>
    )
  }
}

export default connect(({ theme, user }) => ({
  themeList: theme.themeList,
}))(ThemeSelection)
